import { saveAuthData, clearAuthData, getAccessToken } from '../../shared/utils';
import type { AuthModel, LoginRequest } from '../../shared/models';
import {
  getHabitaAuthLoginUrl,
  getHabitaAuthLogoutUrl,
  getHabitaAuthSessionUrl,
  getHabitaAuthUserUrl,
} from './habitaApiUrls';
import { HttpError, HttpRepository } from './HttpRepository';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function extractAuthUser(value: unknown): unknown {
  if (!isRecord(value)) return null;

  const data = isRecord(value.data) ? value.data : {};
  const session = isRecord(value.session) ? value.session : {};
  const dataSession = isRecord(data.session) ? data.session : {};
  const candidates = [
    value.user,
    data.user,
    session.user,
    dataSession.user,
    value.app_metadata || value.id || value.email ? value : null,
  ];

  return candidates.find(isRecord) ?? null;
}

function messageFromError(error: unknown, fallback: string): string {
  if (error instanceof HttpError) {
    const data = isRecord(error.data) ? error.data : {};
    const message = data.msg ?? data.message ?? data.error;
    return typeof message === 'string' && message.trim() ? message : fallback;
  }

  return error instanceof Error && error.message.trim() ? error.message : fallback;
}

export class AuthRepository {
  private httpRepository = new HttpRepository();

  async login(data: LoginRequest): Promise<AuthModel> {
    try {
      const response = await this.httpRepository.post<AuthModel | null>(
        getHabitaAuthLoginUrl(),
        data,
        { authenticated: false }
      );
      const result = response.data;

      if (!result) {
        throw new Error('Erro ao fazer login, revise suas informações');
      }

      saveAuthData(result);

      return result;
    } catch (error) {
      throw new Error(messageFromError(error, 'Erro ao fazer login'));
    }
  }

  /**
   * Realiza o logout do usuário
   */
  async logout(): Promise<{ error?: string }> {
    try {
      await this.httpRepository.post(getHabitaAuthLogoutUrl(), undefined, {
        authenticated: Boolean(getAccessToken()),
      });
      clearAuthData();

      return {};
    } catch (error) {
      clearAuthData();
      return { error: messageFromError(error, 'Erro ao fazer logout') };
    }
  }

  /**
   * Obtém o usuário atual
   */
  async getCurrentUser(): Promise<any> {
    try {
      const response = await this.httpRepository.get<Record<string, unknown> | null>(getHabitaAuthUserUrl());
      return extractAuthUser(response.data);
    } catch {
      return null;
    }
  }

  /**
   * Obtém a sessão atual
   */
  async getCurrentSession(): Promise<any> {
    try {
      const response = await this.httpRepository.get<{ session?: unknown } | null>(getHabitaAuthSessionUrl());
      return response.data?.session ?? null;
    } catch {
      return null;
    }
  }

}
